// File: C:\Users\s-naidu\Desktop\merchant-card\frontend\src\merchant\pages\MerchantSubmitReview.tsx

import React, { useEffect, useMemo, useState } from "react";
import MerchantLayout from "../components/MerchantLayout";
import { apiGet, apiPost } from "../api";

type MerchantMe = {
  merchant_id: string;
  company_name: string;
  status: string; // backend now returns: 未着手 / 進行中 / 提出済（承認待ち） / 承認済

  docs_total: number;
  docs_completed: number;
  docs_percent: number;

  uploads_total: number;
  uploads_completed: number;
  uploads_percent: number;

  created_at: string;
  updated_at: string;

  // submission fields
  is_submitted?: number;
  submitted_at?: string | null;

  // ✅ NEW (backend returns now)
  approval_status?: string; // 未承認 / 承認済
  approved_at?: string | null;
  approved_by?: string | null;
};

type UploadedDocItem = {
  document_key: string;
  document_label: string;
  other_title: string;
  file_name: string;
  file_path: string;
  uploaded_at: string;
};

type FormKey =
  | "company-overview"
  | "representative"
  | "payment-handling"
  | "products"
  | "sales-method"
  | "store-list"
  | "contact-person";

type FormStatusItem = {
  key: FormKey;
  label: string;
  is_completed: boolean;
};

const FORM_LIST: { key: FormKey; label: string }[] = [
  { key: "company-overview", label: "会社概要（全業種共通）" },
  { key: "representative", label: "代表者様情報" },
  { key: "payment-handling", label: "決済取扱状況" },
  { key: "products", label: "取扱商品" },
  { key: "sales-method", label: "販売方法" },
  { key: "store-list", label: "店舗一覧" },
  { key: "contact-person", label: "ご担当者様情報" },
];

// ✅ 3-state for UI colors (authoritative)
type UiState = "normal" | "submitted" | "approved";

function getUiState(me: MerchantMe | null): UiState {
  if (!me) return "normal";

  const s = (me.status || "").trim();
  const ap = (me.approval_status || "").trim();
  const isSub = Number(me.is_submitted || 0) === 1;

  if (s === "承認済" || ap === "承認済") return "approved";
  if (s === "提出済（承認待ち）" || isSub) return "submitted";

  return "normal";
}

function badgeStyle(uiState: UiState, ok: boolean): React.CSSProperties {
  // text stays "完了/未完了" — only color changes by overall state
  // ok=false uses same state color but slightly muted (still readable)
  if (uiState === "approved") {
    return {
      background: ok ? "#16a34a" : "#22c55e",
      color: "#fff",
    };
  }
  if (uiState === "submitted") {
    return {
      background: ok ? "#dc2626" : "#ef4444",
      color: "#fff",
    };
  }
  // normal (未着手/進行中) -> grey
  return {
    background: ok ? "#94a3b8" : "#64748b",
    color: "#fff",
  };
}

function Badge({ ok, uiState }: { ok: boolean; uiState: UiState }) {
  const st = badgeStyle(uiState, ok);
  return (
    <span
      style={{
        padding: "4px 10px",
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 900,
        whiteSpace: "nowrap",
        ...st,
      }}
    >
      {ok ? "完了" : "未完了"}
    </span>
  );
}

export default function MerchantSubmitReview() {
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState("");

  const [me, setMe] = useState<MerchantMe | null>(null);

  // uploaded docs list
  const [uploadedDocs, setUploadedDocs] = useState<UploadedDocItem[]>([]);

  // per-form status (7 forms)
  const [forms, setForms] = useState<FormStatusItem[]>(
    FORM_LIST.map((f) => ({ ...f, is_completed: false }))
  );

  // required docs + missing required
  const [requiredDocLabels, setRequiredDocLabels] = useState<string[] | null>(null);
  const [missingRequiredLabels, setMissingRequiredLabels] = useState<string[] | null>(null);

  const [submitting, setSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState("");

  const uiState = useMemo(() => getUiState(me), [me]);

  const isAlreadySubmitted = useMemo(() => {
    if (!me) return false;
    return Number(me.is_submitted || 0) === 1;
  }, [me]);

  const isApproved = useMemo(() => {
    if (!me) return false;
    const s = (me.status || "").trim();
    const ap = (me.approval_status || "").trim();
    return s === "承認済" || ap === "承認済";
  }, [me]);

  const formsCompletedCount = useMemo(() => forms.filter((f) => f.is_completed).length, [forms]);
  const formsAllDone = useMemo(() => formsCompletedCount === 7, [formsCompletedCount]);

  const requiredDocsDone = useMemo(() => {
    if (missingRequiredLabels !== null) {
      return missingRequiredLabels.length === 0;
    }
    if (!me) return false;
    return me.uploads_completed >= me.uploads_total;
  }, [missingRequiredLabels, me]);

  const canSubmit = useMemo(() => {
    if (!me) return false;
    if (isAlreadySubmitted) return false;
    if (isApproved) return false;
    return formsAllDone && requiredDocsDone;
  }, [me, isAlreadySubmitted, isApproved, formsAllDone, requiredDocsDone]);

  useEffect(() => {
    let alive = true;

    async function load() {
      setLoading(true);
      setPageError("");
      setSubmitMessage("");

      try {
        // 1) merchant meta
        const meRes = await apiGet("/merchant/me");
        const merchant: MerchantMe = meRes?.merchant;
        if (!merchant) throw new Error("加盟店情報の取得に失敗しました。");
        if (!alive) return;
        setMe(merchant);

        // 2) per-form completion
        const formResults: FormStatusItem[] = [];
        for (const f of FORM_LIST) {
          try {
            const r = await apiGet(`/merchant/forms/${f.key}`);
            const isCompleted = Boolean(r?.data?.is_completed);
            formResults.push({ key: f.key, label: f.label, is_completed: isCompleted });
          } catch {
            formResults.push({ key: f.key, label: f.label, is_completed: false });
          }
        }
        if (!alive) return;
        setForms(formResults);

        // 3) documents status
        const statusRes = await apiGet("/merchant/documents/status");

        const items: UploadedDocItem[] = Array.isArray(statusRes?.items) ? statusRes.items : [];
        if (!alive) return;
        setUploadedDocs(items);

        const requiredLabels: string[] | null = Array.isArray(statusRes?.required_labels)
          ? statusRes.required_labels
          : Array.isArray(statusRes?.required)
          ? statusRes.required
              .map((x: any) => String(x?.label || ""))
              .filter((s: string) => s.trim())
          : null;

        const missingLabels: string[] | null = Array.isArray(statusRes?.missing_required_labels)
          ? statusRes.missing_required_labels
          : Array.isArray(statusRes?.missing_required)
          ? statusRes.missing_required
              .map((x: any) => String(x?.label || ""))
              .filter((s: string) => s.trim())
          : null;

        setRequiredDocLabels(requiredLabels);
        setMissingRequiredLabels(missingLabels);
      } catch (e: any) {
        if (!alive) return;
        setPageError(e?.message || "提出内容の取得に失敗しました。");
      } finally {
        if (!alive) return;
        setLoading(false);
      }
    }

    load();
    return () => {
      alive = false;
    };
  }, []);

  const onSubmitAll = async () => {
    setPageError("");
    setSubmitMessage("");

    if (!me) return;

    if (isApproved) {
      setSubmitMessage("すでに承認済みです。");
      return;
    }

    if (isAlreadySubmitted) {
      setSubmitMessage("すでに提出済みです。");
      return;
    }

    if (!canSubmit) {
      setSubmitMessage("未完了の項目があります。フォーム入力と必須書類の提出を完了してください。");
      return;
    }

    const ok = window.confirm("すべてのデータを提出します。提出後は修正できません。よろしいですか？");
    if (!ok) return;

    setSubmitting(true);
    try {
      await apiPost("/merchant/submit", {});
      setSubmitMessage("提出が完了しました。ありがとうございました。");

      // re-fetch merchant status
      const meRes = await apiGet("/merchant/me");
      const merchant: MerchantMe = meRes?.merchant;
      if (merchant) setMe(merchant);

      // re-check docs
      try {
        const statusRes = await apiGet("/merchant/documents/status");
        const items: UploadedDocItem[] = Array.isArray(statusRes?.items) ? statusRes.items : [];
        setUploadedDocs(items);

        const missingLabels: string[] | null = Array.isArray(statusRes?.missing_required_labels)
          ? statusRes.missing_required_labels
          : Array.isArray(statusRes?.missing_required)
          ? statusRes.missing_required
              .map((x: any) => String(x?.label || ""))
              .filter((s: string) => s.trim())
          : null;

        setMissingRequiredLabels(missingLabels);
      } catch {
        // ignore
      }
    } catch (e: any) {
      setPageError(e?.message || "提出に失敗しました。");
    } finally {
      setSubmitting(false);
    }
  };

  // ✅ Submit button colors:
  // - normal: red (ready to submit), grey (disabled)
  // - submitted(waiting): red but disabled
  // - approved: green but disabled
  const submitBtnStyle: React.CSSProperties = {
    padding: "12px 18px",
    borderRadius: 12,
    border: "1px solid rgba(255,255,255,0.18)",
    background:
      uiState === "approved"
        ? "linear-gradient(90deg, #16a34a 0%, #15803d 100%)"
        : uiState === "submitted"
? "linear-gradient(90deg, #22c55e 0%, #16a34a 100%)"

        : !canSubmit || submitting
        ? "linear-gradient(90deg, #94a3b8 0%, #64748b 100%)"
        : "linear-gradient(90deg, #ef4444 0%, #b91c1c 100%)",
    color: "#fff",
    fontWeight: 900,
    cursor: uiState !== "normal" || !canSubmit || submitting ? "not-allowed" : "pointer",
    boxShadow: "0 6px 16px rgba(0,0,0,0.18)",
    minWidth: 140,
  };

  const submitBtnLabel =
    uiState === "approved" ? "承認済" : uiState === "submitted" ? "提出済み" : submitting ? "提出中..." : "全データ提出";

  // ✅ Info box (the one you showed green)
  // now: grey/red/green based on uiState
  const infoBoxStyle: React.CSSProperties =
    uiState === "approved"
      ? { background: "#ecfdf5", border: "1px solid #86efac", color: "#166534" }
      : uiState === "submitted"
      ? { background: "#fff1f2", border: "1px solid #fecdd3", color: "#b91c1c" }
      : { background: "#f1f5f9", border: "1px solid #cbd5e1", color: "#334155" };

  const infoBoxText =
    uiState === "approved"
      ? "承認済みです。"
      : uiState === "submitted"
      ? "提出済（承認待ち）です。提出後は編集できません。"
      : "";

  return (
    <MerchantLayout title="提出内容確認" active="submitReview">
      <div style={{ fontWeight: 900, fontSize: 18, marginBottom: 10 }}>提出内容確認</div>

      <div style={{ color: "#475569", marginBottom: 12 }}>
        フォーム入力と書類アップロードの内容を確認し、すべて完了したら提出してください。
      </div>

      {pageError && (
        <div
          style={{
            marginBottom: 14,
            background: "#fff1f2",
            border: "1px solid #fecdd3",
            color: "#9f1239",
            padding: "10px 12px",
            borderRadius: 12,
            fontWeight: 900,
            fontSize: 13,
            lineHeight: 1.6,
          }}
        >
          {pageError}
        </div>
      )}

      {submitMessage && (
        <div
          style={{
            marginBottom: 14,
            background: "#eff6ff",
            border: "1px solid #bfdbfe",
            color: "#1e3a8a",
            padding: "10px 12px",
            borderRadius: 12,
            fontWeight: 900,
            fontSize: 13,
            lineHeight: 1.6,
          }}
        >
          {submitMessage}
        </div>
      )}

      {loading ? (
        <div style={{ padding: "12px 0", color: "#334155", fontWeight: 900 }}>読み込み中...</div>
      ) : (
        <>
          {/* Summary */}
          {me && (
            <div
              style={{
                border: "1px solid #e2e8f0",
                borderRadius: 12,
                padding: 14,
                background: "#fff",
                marginBottom: 14,
              }}
            >
              <div style={{ fontWeight: 900, marginBottom: 8 }}>進捗サマリー</div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div
                  style={{
                    border: "1px solid #e2e8f0",
                    borderRadius: 12,
                    padding: 12,
                    background: "#f8fafc",
                  }}
                >
                  <div style={{ fontWeight: 900, marginBottom: 6 }}>フォーム入力（7件）</div>
                  <div style={{ fontWeight: 900, color: "#0b1f3b" }}>{formsCompletedCount}/7</div>
                </div>

                <div
                  style={{
                    border: "1px solid #e2e8f0",
                    borderRadius: 12,
                    padding: 12,
                    background: "#f8fafc",
                  }}
                >
                  <div style={{ fontWeight: 900, marginBottom: 6 }}>必須書類</div>

                  {missingRequiredLabels !== null ? (
                    <div style={{ fontWeight: 900, color: requiredDocsDone ? "#16a34a" : "#b45309" }}>
                      {requiredDocsDone ? "すべて提出済" : `未提出：${missingRequiredLabels.length}件`}
                    </div>
                  ) : (
                    <div style={{ fontWeight: 900, color: "#0b1f3b" }}>
                      {me.uploads_completed}/{me.uploads_total}（{me.uploads_percent}%）
                    </div>
                  )}

                  {requiredDocLabels && (
                    <div style={{ marginTop: 6, fontSize: 12, color: "#64748b", fontWeight: 900 }}>
                      必須：{requiredDocLabels.length}件
                    </div>
                  )}
                </div>
              </div>

              <div style={{ marginTop: 10, fontSize: 12, color: "#64748b", fontWeight: 900 }}>
                最終更新：{me.updated_at}
              </div>

              {/* ✅ THIS BOX changes grey/red/green */}
              {(uiState === "submitted" || uiState === "approved") && (
                <div
                  style={{
                    marginTop: 10,
                    padding: "10px 12px",
                    borderRadius: 12,
                    fontWeight: 900,
                    fontSize: 13,
                    ...infoBoxStyle,
                  }}
                >
                  {infoBoxText}
                </div>
              )}
            </div>
          )}

          {/* Form status list (7 forms) */}
          <div
            style={{
              border: "1px solid #e2e8f0",
              borderRadius: 12,
              padding: 14,
              background: "#fff",
              marginBottom: 14,
            }}
          >
            <div style={{ fontWeight: 900, marginBottom: 10 }}>フォーム入力状況（7フォーム）</div>

            <div style={{ display: "grid", gap: 10 }}>
              {forms.map((f) => (
                <div
                  key={f.key}
                  style={{
                    border: "1px solid #e2e8f0",
                    borderRadius: 12,
                    padding: 12,
                    background: "#f8fafc",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 10,
                  }}
                >
                  <div style={{ fontWeight: 900, color: "#0f172a" }}>{f.label}</div>

                  {/* ✅ Only the color box changes by state */}
                  <Badge ok={f.is_completed} uiState={uiState} />
                </div>
              ))}
            </div>

            {!formsAllDone && (
              <div style={{ marginTop: 10, fontSize: 12, color: "#64748b", fontWeight: 900 }}>
                ※ 未完了のフォームがあります。各フォームを開いて「保存」してください。
              </div>
            )}
          </div>

          {/* Required missing list */}
          {missingRequiredLabels !== null && (
            <div
              style={{
                border: "1px solid #e2e8f0",
                borderRadius: 12,
                padding: 14,
                background: "#fff",
                marginBottom: 14,
              }}
            >
              <div style={{ fontWeight: 900, marginBottom: 10 }}>必須書類の未提出</div>

              {missingRequiredLabels.length === 0 ? (
                <div style={{ color: "#166534", fontWeight: 900, fontSize: 13 }}>
                  すべての必須書類が提出済みです。
                </div>
              ) : (
                <div style={{ display: "grid", gap: 8 }}>
                  {missingRequiredLabels.map((label) => (
                    <div
                      key={label}
                      style={{
                        border: "1px solid #fed7aa",
                        background: "#fffbeb",
                        borderRadius: 12,
                        padding: "10px 12px",
                        fontWeight: 900,
                        color: "#92400e",
                      }}
                    >
                      未提出：{label}
                    </div>
                  ))}
                </div>
              )}

              <div style={{ marginTop: 10, fontSize: 12, color: "#64748b", fontWeight: 900 }}>
                ※ 必須書類は「書類アップロード」から提出してください。
              </div>
            </div>
          )}

          {/* Uploaded documents list */}
          <div
            style={{
              border: "1px solid #e2e8f0",
              borderRadius: 12,
              padding: 14,
              background: "#fff",
              marginBottom: 14,
            }}
          >
            <div style={{ fontWeight: 900, marginBottom: 10 }}>提出済みの書類</div>

            {uploadedDocs.length === 0 ? (
              <div style={{ color: "#64748b", fontWeight: 900, fontSize: 13 }}>
                提出済みの書類がまだありません。
              </div>
            ) : (
              <div style={{ display: "grid", gap: 10 }}>
                {uploadedDocs.map((d) => (
                  <div
                    key={d.document_key}
                    style={{
                      border: "1px solid #e2e8f0",
                      borderRadius: 12,
                      padding: 12,
                      background: "#f8fafc",
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                      <div style={{ fontWeight: 900, color: "#0f172a" }}>
                        {d.document_label}
                        {d.other_title ? `（${d.other_title}）` : ""}
                      </div>

                      {/* ✅ This pill also becomes grey/red/green by state */}
                      <span
                        style={{
                          padding: "4px 10px",
                          borderRadius: 999,
                          fontSize: 12,
                          fontWeight: 900,
                          whiteSpace: "nowrap",
                          ...(badgeStyle(uiState, true)),
                        }}
                      >
                        提出済
                      </span>
                    </div>

                    <div style={{ marginTop: 6, fontSize: 12, color: "#334155", fontWeight: 900 }}>
                      ファイル：{d.file_name}
                    </div>
                    <div style={{ marginTop: 4, fontSize: 12, color: "#64748b", fontWeight: 900 }}>
                      提出日時：{d.uploaded_at}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Submit block */}
          <div
            style={{
              border: "1px solid #e2e8f0",
              borderRadius: 12,
              padding: 14,
              background: "#fff",
              marginBottom: 6,
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
              <div>
                <div style={{ fontWeight: 900, fontSize: 18, marginBottom: 6 }}>全データ提出</div>
                <div style={{ color: "#475569", fontWeight: 900, fontSize: 13, lineHeight: 1.6 }}>
                  フォーム入力と書類アップロードがすべて完了したら提出してください。
                  <br />
                  <span style={{ color: "#b91c1c" }}>提出後は編集できません。</span>
                </div>
              </div>

              <button
                onClick={onSubmitAll}
                disabled={uiState !== "normal" || !canSubmit || submitting}
                style={submitBtnStyle}
                title={
                  uiState === "approved"
                    ? "承認済みです"
                    : uiState === "submitted"
                    ? "提出済みです"
                    : canSubmit
                    ? "すべてのデータを提出します"
                    : "未完了の項目があります"
                }
              >
                {submitBtnLabel}
              </button>
            </div>

            {!canSubmit && uiState === "normal" && (
              <div style={{ marginTop: 10, fontSize: 12, color: "#b45309", fontWeight: 900 }}>
                ※ 提出するには「フォーム入力」と「書類アップロード（必須）」がすべて完了している必要があります。
              </div>
            )}

           {uiState !== "normal" && (
  <div
    style={{
      marginTop: 10,
      background: "#ecfdf5",
      border: "1px solid #86efac",
      color: "#166534",
      padding: "10px 12px",
      borderRadius: 12,
      fontWeight: 900,
      fontSize: 13,
    }}
  >
    {uiState === "approved"
      ? "承認済みです。編集はできません。"
      : "提出済（承認待ち）です。編集はできません。"}
  </div>
)}

          </div>
        </>
      )}
    </MerchantLayout>
  );
}
